import { useId, type ReactNode } from 'react';
import { cx } from '../cx';

export type RadioOption = { value: string; label: ReactNode; description?: ReactNode; disabled?: boolean };

export type RadioGroupProps = {
  /** Group label (a fieldset legend), required so screen readers announce what is being chosen. */
  legend: ReactNode;
  legendHidden?: boolean;
  options: RadioOption[];
  value: string;
  onValueChange: (value: string) => void;
  /** `list` for forms; `cards` for a choice between two to four described options. */
  variant?: 'list' | 'cards';
  name?: string;
  className?: string;
};

/**
 * Native radios (so arrow keys, form submission and screen readers work as expected), restyled.
 * Cards variant fills the chosen card with the selected colour and shows the focus ring on it.
 */
export function RadioGroup({
  legend,
  legendHidden,
  options,
  value,
  onValueChange,
  variant = 'list',
  name,
  className,
}: RadioGroupProps) {
  const auto = useId();
  const groupName = name ?? `rg${auto.replace(/:/g, '')}`;
  return (
    <fieldset className={cx('m-0 min-w-0 border-0 p-0', className)}>
      <legend className={cx('mb-2 p-0 text-sm font-semibold text-jc-ink', legendHidden && 'sr-only')}>{legend}</legend>
      <div className={variant === 'cards' ? 'flex flex-wrap gap-2' : 'flex flex-col gap-2'}>
        {options.map((o) =>
          variant === 'cards' ? (
            <label
              key={o.value}
              className={cx(
                'relative flex min-w-36 flex-1 flex-col gap-0.5 rounded-jc-field bg-jc-surface px-3.5 py-3 text-jc-ink ring-1 ring-inset ring-jc-border-control transition-colors duration-(--jc-duration-fast)',
                'has-checked:bg-jc-selected has-checked:text-jc-on-selected has-checked:ring-0',
                'has-focus-visible:ring-2 has-focus-visible:ring-jc-focus has-focus-visible:ring-offset-2 has-focus-visible:ring-offset-jc-page',
                o.disabled
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer hover:bg-jc-elevated has-checked:hover:bg-jc-selected',
              )}
            >
              <input
                type="radio"
                name={groupName}
                value={o.value}
                checked={value === o.value}
                disabled={o.disabled}
                onChange={() => onValueChange(o.value)}
                className="absolute opacity-0"
              />
              <span className="text-sm font-semibold">{o.label}</span>
              {o.description ? <span className="text-sm opacity-80">{o.description}</span> : null}
            </label>
          ) : (
            <label
              key={o.value}
              className={cx(
                'inline-flex items-start gap-3 text-sm text-jc-ink',
                o.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
              )}
            >
              <span className="relative mt-px grid size-5.5 shrink-0 place-items-center">
                <input
                  type="radio"
                  name={groupName}
                  value={o.value}
                  checked={value === o.value}
                  disabled={o.disabled}
                  onChange={() => onValueChange(o.value)}
                  className={cx(
                    'peer absolute inset-0 m-0 cursor-pointer appearance-none disabled:cursor-not-allowed rounded-full border border-jc-border-control bg-jc-surface',
                    'transition-colors duration-(--jc-duration-fast) checked:border-jc-selected checked:bg-jc-selected',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus focus-visible:ring-offset-2 focus-visible:ring-offset-jc-page',
                  )}
                />
                <span className="pointer-events-none relative size-2 rounded-full bg-jc-on-selected opacity-0 peer-checked:opacity-100" />
              </span>
              <span className="flex flex-col gap-0.5">
                <span>{o.label}</span>
                {o.description ? <span className="text-sm text-jc-ink-secondary">{o.description}</span> : null}
              </span>
            </label>
          ),
        )}
      </div>
    </fieldset>
  );
}
